// src/pages/Exams/generate/GenerateExamPage.tsx
import { QuickModeForm } from './QuickModeForm';
import { GenerationProgress } from './GenerationProgress';
import { ReviewStep } from './ReviewStep';
import { CoverageReport } from './CoverageReport';
import { SaveBar } from './SaveBar';
import { TemplatesTab } from './TemplatesTab';
import { HistoryTab } from './HistoryTab';
import { useGenerationJob } from './hooks/useGenerationJob';
import { useExamTemplates } from './hooks/useExamTemplates';
import { useSchoolChapters } from './hooks/useSchoolChapters';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useCycle } from '../../../school_panel/api/hooks';
import { useSchoolGrades, useSchoolGradeSubjects } from '../../../react-query/queries';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { Link } from 'react-router-dom';
import { PageHeader, PanelCard, Segmented, EmptyState, Button, ConfirmDialog } from '../../../school_panel/components/ui';
import { ArrowLeft, PlusCircle, TriangleAlert } from 'lucide-react';
import { GLOSSARY } from '../../../school_panel/lib/glossary';
import { LINK_BTN } from '../../../school_panel/components/ui/linkButton';
import { withTime } from '../../../school_panel/lib/dates';

type TabKey = 'generate' | 'templates' | 'history';

const TABS: Array<{ key: TabKey; label: string; help: 'tab_generate' | 'templates' | 'history' }> = [
  { key: 'generate',  label: 'Generate',  help: 'tab_generate' },
  { key: 'history',   label: 'History',   help: 'history' },
  { key: 'templates', label: 'Templates', help: 'templates' },
];

export default function GenerateExamPage() {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const [previewMode, setPreviewMode] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [chapterFilter, setChapterFilter] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabKey = rawTab === 'templates' || rawTab === 'history' ? rawTab : 'generate';
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'generate') next.delete('tab'); else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };
  const templates = useExamTemplates();

  const cycleParam = searchParams.get('cycle');
  const parsedCycle = cycleParam ? Number(cycleParam) : null;
  // ?cycle=abc parses to NaN: the query stays disabled and never reports an
  // error, so without this the banner would never appear and the page would
  // look like a plain generation run.
  const cycleId = parsedCycle != null && Number.isFinite(parsedCycle) && parsedCycle > 0 ? parsedCycle : null;
  const cycleUnparsable = cycleParam != null && cycleId == null;
  const cycleQ = useCycle(cycleId);
  const cycleUsable = !!cycleQ.data && cycleQ.data.status === 'diagnosed';
  // While the cycle is still being fetched, cycleUsable is (correctly) false,
  // which would otherwise let the default/empty form render and be submitted
  // without cycle_id before we know whether the cycle applies. Block the form
  // itself during this window instead of just racing the banner.
  const cycleLoading = cycleId != null && cycleQ.isLoading;
  const job = useGenerationJob(
    jobIdParam ? Number(jobIdParam) : undefined,
    cycleUsable ? cycleId : null,
  );
  useEffect(() => {
    if (cycleUsable && cycleQ.data && job.phase === 'idle') {
      job.loadBlueprint(cycleQ.data.blueprint as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleUsable, cycleQ.data?.id]);

  const { token } = useAuth();
  const jobGradeId = job.job?.blueprint?.grade_id ?? null;
  // Names are only needed for the job header; a null token keeps both queries
  // from sending a request in any other state.
  const nameToken = tab === 'generate' && job.phase !== 'idle' && jobGradeId ? token : null;
  const { data: gradesData } = useSchoolGrades(nameToken);
  const { data: subjectsData } = useSchoolGradeSubjects(nameToken, jobGradeId ? String(jobGradeId) : '0');

  const subjectId = job.job?.blueprint?.subject_id ?? job.blueprint.subject_id;
  const chaptersQ = useSchoolChapters(subjectId ?? null);
  const chapterNames = useMemo(() => {
    const m: Record<number, string> = {};
    (chaptersQ.data ?? []).forEach(c => { m[c.id] = c.name; });
    return m;
  }, [chaptersQ.data]);
  const formLabels = useMemo(
    () => Array.from(new Set(job.draftQuestions.map(q => q.form_label || 'A'))).sort(),
    [job.draftQuestions]
  );

  const showConfig = job.phase === 'idle';
  const showProgress = job.phase === 'queued'
    || job.phase === 'fetching_chunks'
    || job.phase === 'generating'
    || job.phase === 'scoring'
    || job.phase === 'cancelled';
  const showReview = job.phase === 'review_pending';
  const showFailed = job.phase === 'failed';
  // Review with nothing in it (and nothing to undo): every chapter came back
  // empty, so there is nothing to review or save.
  const nothingDelivered = showReview && job.draftQuestions.length === 0 && !job.canUndoRemove;
  const noSourceCount = (job.job?.failed_chapters ?? []).filter(f => f.reason === 'no_source').length;

  // "Start over" after a failure: forget the remembered job and load the page
  // fresh, which is what resets the blueprint and the hook's job state (a plain
  // navigate would keep this component mounted when the failure happened on
  // /exams/generate). The query string rides along so ?cycle= / ?tab= survive.
  const startOver = () => {
    try { sessionStorage.removeItem('exam_gen_active_job'); } catch { /* ignore */ }
    const qs = searchParams.toString();
    window.location.assign(`/exams/generate${qs ? `?${qs}` : ''}`);
  };

  // Job header (design: GenerateJob): once a job exists the page names it and
  // offers a way back; the sub line reads straight off the job and its blueprint.
  const inJob = tab === 'generate' && !!job.job && !showConfig;
  const jobBlueprint = job.job?.blueprint;
  const jobGrade: string | undefined = (() => {
    const g = gradesData?.grades?.find((x: any) => x.id === jobBlueprint?.grade_id);
    return g?.grade_name ?? g?.name;
  })();
  const jobSubject: string | undefined = (() => {
    const x = subjectsData?.subjects?.find((y: any) => y.id === jobBlueprint?.subject_id);
    return x?.subject_name ?? x?.name;
  })();
  const startedIso = job.job?.started_at ?? job.job?.created_at;
  const startedAt = startedIso ? new Date(startedIso) : null;
  const questionCount = showReview ? job.draftQuestions.length : (jobBlueprint?.total_questions ?? 0);
  const jobSubtitle = [
    jobGrade,
    jobSubject,
    jobBlueprint?.num_forms ? `${jobBlueprint.num_forms} form${jobBlueprint.num_forms === 1 ? '' : 's'}` : null,
    startedAt && !Number.isNaN(startedAt.getTime())
      ? `started ${withTime(startedAt, { day: 'numeric', month: 'short' })}`
      : null,
    questionCount > 0 ? `${questionCount} question${questionCount === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  if (tab === 'generate' && job.phase === 'saved' && job.job?.exam_set_id != null) {
    return <Navigate to={`/exams/exam-set/${job.job.exam_set_id}`} replace />;
  }

  // Design `.seg` view switcher (Generate / Templates / History) in the header row.
  const viewSeg = (
    <Segmented<TabKey> label="Generate exam views" tabs
      options={TABS.map(t => ({ key: t.key, label: t.label, help: GLOSSARY[t.help].def }))}
      value={tab} onChange={setTab} />
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-4">
      {inJob && job.job ? (
        <PageHeader
          eyebrow={<><Link to="/exams/generate" className="text-inherit hover:underline">Generate exam</Link> · Job #{job.job.id}</>}
          title={jobBlueprint?.name?.trim() || 'Generate exam'}
          subtitle={jobSubtitle || undefined}
          actions={
            <>
              <Button variant="ghost"
                onClick={() => (job.phase === 'review_pending' ? setConfirmNew(true) : startOver())}
                icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />}>
                Back to set-up
              </Button>
            </>
          }
        />
      ) : (
        <PageHeader
          eyebrow="Exams · AI generation"
          title="Generate an exam"
          subtitle="Draft a full exam with AI from your chapters. You review every question before it is saved."
          actions={
            <>
            {viewSeg}
            <Link
              to="/exams/add-exam"
              className={LINK_BTN}>
              <PlusCircle size={18} strokeWidth={1.8} aria-hidden />
              Write questions manually
            </Link>
            </>
          }
        />
      )}
      <ConfirmDialog
        isOpen={confirmNew}
        title="Start a new generation?"
        body="Unsaved review edits on this job will be lost. The job stays in History."
        confirmLabel="Start new"
        danger={false}
        onCancel={() => setConfirmNew(false)}
        onConfirm={() => { setConfirmNew(false); startOver(); }} />

      {cycleId != null && cycleQ.isSuccess && cycleUsable && (
        <div className="mb-4 rounded-[12px] border border-accent bg-accent-bg px-3.5 py-2.5 text-sm text-ink">
          Remedial exam for cycle #{cycleQ.data!.id} · {cycleQ.data!.target_chapters.map(c => c.name).filter(Boolean).join(', ')} · {cycleQ.data!.target_count} students
        </div>
      )}
      {(cycleUnparsable || (cycleId != null && (cycleQ.isError || (cycleQ.isSuccess && !cycleUsable)))) && (
        <div className="mb-4 rounded-[12px] border border-warning/40 bg-warning-bg px-3.5 py-2.5 text-sm text-warning-ink">
          Cycle not available — generating a normal exam instead.
        </div>
      )}

      {tab === 'generate' && jobIdParam && job.loadError && showConfig && (
        <div role="alert" className="mb-4 rounded-[12px] border border-warning/40 bg-warning-bg px-3.5 py-2.5 text-sm text-warning-ink">
          That generation job could not be opened. It may belong to another teacher.
        </div>
      )}

      {tab === 'templates' && (
        <TemplatesTab onUse={(bp) => { job.loadBlueprint(bp); setTab('generate'); }} />
      )}
      {tab === 'history' && <HistoryTab />}

      {tab === 'generate' && (
        <div className="space-y-[18px]">
          {showConfig && cycleLoading && (
            <PanelCard>
              <div className="rounded-[12px] border border-line bg-surface-2 px-3 py-6 text-center text-sm text-muted">
                Loading cycle…
              </div>
            </PanelCard>
          )}
          {showConfig && !cycleLoading && (
            <QuickModeForm
              blueprint={job.blueprint}
              patch={job.patchBlueprint}
              onStart={job.startGeneration}
              startError={job.startError}
              onSaveTemplate={(name) => templates.createTemplate(name, job.blueprint)}
              templates={templates.templates}
              onLoadTemplate={job.loadBlueprint}
            />
          )}
          {(showProgress || showFailed) && job.job && (
            <GenerationProgress
              job={job.job}
              onCancel={job.cancelGeneration}
              onStartOver={startOver}
            />
          )}
          {nothingDelivered && (
            <PanelCard>
              <EmptyState
                icon={<TriangleAlert size="1em" />}
                title="No questions could be generated"
                body={noSourceCount > 0 || (job.job?.failed_chapters ?? []).length === 0
                  ? 'These chapters have no textbook content linked, so the AI had nothing to ground questions in.'
                  : 'The AI could not produce questions for these chapters. Try again or adjust the set-up.'}
              />
              <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-line pt-4">
                <p className="text-xs text-muted">Nothing to save yet.</p>
                <Link to="/school-setup/chapters" className="text-sm font-semibold text-slate hover:underline">Open Chapter Setup</Link>
                <Button onClick={startOver}>Back to set-up</Button>
              </div>
            </PanelCard>
          )}
          {showReview && job.job && !nothingDelivered && (
            <>
              <GenerationProgress job={job.job} onCancel={job.cancelGeneration}>
                <CoverageReport
                  requestedTotal={(job.job.blueprint ?? job.blueprint).total_questions}
                  blueprint={job.job.blueprint ?? job.blueprint}
                  questions={job.draftQuestions}
                  chapterNames={chapterNames}
                  failedChapters={job.job.failed_chapters}
                  formLabels={formLabels}
                  onFilterChapter={setChapterFilter}
                  onRetryChapter={job.isBulkRegenerating ? undefined : (form, chapterId) =>
                    job.retryChapter(form, chapterId).catch((e) => { console.error('[exam_gen] retry chapter failed', e); })}
                />
              </GenerationProgress>
              <ReviewStep
                job={job.job}
                questions={job.draftQuestions}
                previewMode={previewMode}
                onTogglePreview={setPreviewMode}
                onUpdate={job.updateQuestion}
                onRemove={job.removeQuestion}
                onRegenerate={job.regenerateQuestion}
                canUndoRemove={job.canUndoRemove}
                onUndoRemove={job.undoRemove}
                activeChapterFilter={chapterFilter}
                chapterNames={chapterNames}
                onSetChapterFilter={setChapterFilter}
                onToggleFlag={job.toggleFlag}
                onBulkRegenerate={job.bulkRegenerate}
                isBulkRegenerating={job.isBulkRegenerating}
                lastRegen={job.lastRegen}
                onUndoRegenerate={job.undoRegenerate}
                onClearLastRegen={job.clearLastRegen}
              />
              <SaveBar
                job={job.job}
                questions={job.draftQuestions}
                onSave={job.saveAsExamSet}
                error={job.saveError}
                conflict={job.saveConflict}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
